import { useCallback, useMemo } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import { setPeriod } from '@/modules/analytics/redux/actions';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { urlSafeBase64Decode, urlSafeBase64Encode } from '@/util/util';

import {
  type DateRange,
  type DateSelection,
  parseDateSelection,
  periodToSelection,
} from '../dateSelection';
import { Period } from '../types';
import { getPeriodDates } from '../utils';

export const useDateFilter = (setDateOpen) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [urlParams] = useSearchParams();

  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const selection = useMemo((): DateSelection | undefined => {
    const dateParam = urlParams.get('date');
    if (!dateParam) {
      return undefined;
    }
    try {
      return parseDateSelection(JSON.parse(urlSafeBase64Decode(dateParam)).selection);
    } catch {
      return undefined;
    }
  }, [urlParams]);

  const applyRange = useCallback(
    (range: DateRange, intent?: DateSelection) => {
      dispatch(setPeriod(range));

      const params = new URLSearchParams();
      const nextSelection: DateSelection = intent ?? { type: 'custom', ...range };
      params.set(
        'date',
        urlSafeBase64Encode(JSON.stringify({ ...range, selection: nextSelection }))
      );
      const filters = urlParams.get('filters');
      if (!isEmpty(filters)) {
        params.set('filters', filters);
      }
      navigate({ search: params.toString() }, { replace: true });
      setDateOpen(false);
    },
    [dispatch, navigate, setDateOpen, urlParams]
  );

  const handlePeriodClick = useCallback(
    (period: Period) =>
      applyRange(
        getPeriodDates(startDate, endDate, period),
        periodToSelection(period) ?? undefined
      ),
    [applyRange, endDate, startDate]
  );

  return { startDate, endDate, selection, applyRange, handlePeriodClick };
};
